import { useState, FC } from "react";

import { Button, Modal, Tab, Nav } from "react-bootstrap";
import { toast } from "react-toastify";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";

import { getDateFromIso } from "@/_helpers/stringHelper.ts";
import { Comment } from "@/common/type/sharedTypes.ts";
import {
  createServiceComment,
  deleteServiceComment,
  updateServiceComment,
} from "@/modules/order/services";

interface IOrderCommentsProps {
  comments?: Array<Comment>;
  idService: string;
  canEditComments: boolean;
}

const OrderComments: FC<IOrderCommentsProps> = ({
  canEditComments,
  comments,
  idService,
}) => {
  /*hasSuperAdminPermission*/
  /*super admin admin groupe admin agence*/

  // Define custom styles for the tabs
  const tabsStyles = {
    tabContainer: {
      display: "flex",
      flexDirection: "column" as const,
      borderRadius: "0.25rem",
    },
    navItem: {
      margin: 0,
    },
    navLink: {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
      position: "relative" as const,
      margin: 0,
      paddingBottom: "8px",
    },
    activeNavLink: {
      borderBottomColor: "transparent",
      backgroundColor: "#fff",
      borderBottom: "0px",
    },
    tabContent: {
      border: "1px solid #dee2e6",
      padding: "15px",
      marginTop: -1, // Pull content up to overlap with the tab border
      borderTopLeftRadius: 0, // Remove top-left radius where active tab connects
      borderBottomLeftRadius: "0.25rem",
      borderBottomRightRadius: "0.25rem",
      backgroundColor: "#fff",
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    },
  };

  const [newComment, setNewComment] = useState("");
  const [newExternalComment, setNewExternalComment] = useState("");
  const [activeTab, setActiveTab] = useState("internal");
  const queryClient = useQueryClient();

  const [onEdit, setOnEdit] = useState<{
    id: string;
    comment: string;
  } | null>(null);
  const [onDelete, setOnDelete] = useState<null | string>(null);

  const addComment = useMutation({
    mutationFn: () => {
      const commentText =
        activeTab === "external" ? newExternalComment : newComment;
      const isExternalComment = activeTab === "external";
      return createServiceComment(idService, commentText, isExternalComment);
    },
    onError: (error) => {
      console.error(error);
      toast.error("Erreur lors de l'ajout du commentaire");
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getServiceById", idService],
      });
      if (activeTab === "external") {
        setNewExternalComment("");
      } else {
        setNewComment("");
      }
      toast.success("Commentaire ajouté avec succès");
    },
  });

  const modifyComment = useMutation({
    mutationFn: (data: { id: string; comment: string }) =>
      updateServiceComment(
        idService,
        data.id,
        data.comment,
        Boolean(activeTab === "external")
      ),
    onError: (error) => {
      console.error(error);
      toast.error("Erreur lors de la modification du commentaire");
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getServiceById", idService],
      });
      setNewComment("");
      setNewExternalComment("");
      setOnEdit(null);
      toast.success("Commentaire modifié avec succès");
    },
  });

  const deleteComment = useMutation({
    mutationFn: (data: { id: string }) =>
      deleteServiceComment(idService, data.id),
    onError: (error) => {
      console.error(error);
      toast.error("Erreur lors de la suppression du commentaire");
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getServiceById", idService],
      });
      setNewComment("");
      setNewExternalComment("");
      setOnEdit(null);
      setOnDelete(null);
      toast.success("Commentaire supprimé avec succès");
    },
  });

  return (
    <>
      <Modal
        centered
        show={!!onDelete}
        onHide={() => !deleteComment.isLoading && setOnDelete(null)}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Suppression d'un commentaire</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Êtes-vous sûr de vouloir supprimer ce commentaire ?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setOnDelete(null)}
            disabled={deleteComment.isLoading}
          >
            Annuler
          </Button>
          <Button
            variant="danger"
            onClick={async () => {
              if (onDelete) {
                await deleteComment.mutateAsync({
                  id: onDelete,
                });
              }
            }}
            disabled={deleteComment.isLoading}
          >
            {deleteComment.isLoading ? "Suppression..." : "Supprimer"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Tab.Container
        id="comments-tabs"
        defaultActiveKey="internal"
        onSelect={(key) => {
          setActiveTab(key || "internal");
          setOnEdit(null);
        }}
      >
        <div className="tab-wrapper" style={tabsStyles.tabContainer}>
          <Nav variant="tabs" className="border-bottom-0 nav-seamless mb-0">
            <Nav.Item style={tabsStyles.navItem}>
              <Nav.Link
                eventKey="internal"
                style={{
                  ...tabsStyles.navLink,
                  ...(activeTab === "internal" ? tabsStyles.activeNavLink : {}),
                }}
                className={`${activeTab === "internal" ? "active border-bottom-0 fw-bold" : ""}`}
              >
                Informations complémentaires du dossier
              </Nav.Link>
            </Nav.Item>
            <Nav.Item style={tabsStyles.navItem}>
              <Nav.Link
                eventKey="external"
                style={{
                  ...tabsStyles.navLink,
                  ...(activeTab === "external" ? tabsStyles.activeNavLink : {}),
                }}
                className={`${activeTab === "external" ? "active border-bottom-0 fw-bold" : ""}`}
              >
                Messagerie
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content style={tabsStyles.tabContent} className="shadow-sm">
            <Tab.Pane eventKey="internal">
              <div className="d-flex flex-column gap-3">
                {!comments ||
                comments.filter((c) => !c.isExternal).length < 1 ? (
                  <p className="p-no-property mt-3">
                    Aucunes informations complémentaires
                  </p>
                ) : (
                  <>
                    {comments
                      .filter((comment) => !comment.isExternal)
                      .map((comment: Comment, index: number) => {
                        return (
                          <div key={index} className="d-flex flex-column gap-3">
                            <div className="d-flex gap-1 flex-column">
                              <div className={"d-flex"}>
                                <span>
                                  <i
                                    className="pi pi-lock"
                                    style={{ color: "slateblue" }}
                                  ></i>
                                </span>
                                <span
                                  className={"text-primary mx-2"}
                                  style={{
                                    fontWeight: "bold",
                                  }}
                                >
                                  {comment.user.lastname}{" "}
                                  {comment.user.firstname}
                                </span>
                                {canEditComments ? (
                                  <>
                                    {onEdit?.id === comment.id ? (
                                      <Button
                                        disabled={
                                          modifyComment.isLoading ||
                                          addComment.isLoading ||
                                          deleteComment.isLoading
                                        }
                                        size={"sm"}
                                        variant="outline-secondary"
                                        className="btn btn-actions ms-2 pt-2"
                                        onClick={() => {
                                          setOnEdit(null);
                                        }}
                                      >
                                        <i className="bi bi-x-lg" />
                                      </Button>
                                    ) : (
                                      <Button
                                        disabled={
                                          modifyComment.isLoading ||
                                          addComment.isLoading ||
                                          deleteComment.isLoading
                                        }
                                        size={"sm"}
                                        variant="outline-secondary"
                                        className="btn btn-actions ms-2 pt-2"
                                        onClick={() => {
                                          setOnEdit({
                                            id: comment.id ?? "",
                                            comment: comment.comment,
                                          });
                                        }}
                                      >
                                        <i className="bi bi-pencil-square" />
                                      </Button>
                                    )}
                                    <Button
                                      size={"sm"}
                                      disabled={
                                        modifyComment.isLoading ||
                                        addComment.isLoading ||
                                        deleteComment.isLoading
                                      }
                                      variant={"outline-danger"}
                                      className="btn btn-actions ms-2 pt-2"
                                      onClick={() =>
                                        setOnDelete(comment.id ?? null)
                                      }
                                    >
                                      <i className="bi bi-trash3"></i>
                                    </Button>
                                  </>
                                ) : null}
                              </div>
                              <label
                                style={{
                                  fontSize: "0.8rem",
                                  color: "#6c757d",
                                }}
                              >
                                le {getDateFromIso(comment.createdAt)} à{" "}
                                {DateTime.fromISO(comment.createdAt).toFormat(
                                  "HH:mm"
                                )}
                              </label>
                            </div>
                            <div className={"position-relative"}>
                              {onEdit?.id === comment.id ? (
                                <div>
                                  <textarea
                                    onChange={(e) =>
                                      onEdit &&
                                      setOnEdit({
                                        ...onEdit,
                                        comment: e.target.value,
                                      })
                                    }
                                    rows={4}
                                    className={`form-input_field w-100 p-2`}
                                    placeholder={"Ajouter un commentaire"}
                                  >
                                    {onEdit?.comment}
                                  </textarea>
                                  <div
                                    className={
                                      "d-flex justify-content-start mt-1"
                                    }
                                  >
                                    <Button
                                      disabled={
                                        modifyComment.isLoading ||
                                        addComment.isLoading ||
                                        deleteComment.isLoading
                                      }
                                      variant="primary"
                                      size="sm"
                                      onClick={() =>
                                        onEdit &&
                                        modifyComment.mutate({
                                          id: onEdit.id,
                                          comment: onEdit.comment,
                                        })
                                      }
                                    >
                                      {modifyComment.isLoading
                                        ? "Modification..."
                                        : "Modifier"}
                                    </Button>
                                    <Button
                                      disabled={
                                        modifyComment.isLoading ||
                                        addComment.isLoading ||
                                        deleteComment.isLoading
                                      }
                                      variant="secondary"
                                      className={"ms-2"}
                                      size="sm"
                                      onClick={() => setOnEdit(null)}
                                    >
                                      Annuler
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <p>{comment.comment}</p>
                              )}
                            </div>
                            <div className="border-bottom" />
                          </div>
                        );
                      })}
                  </>
                )}

                {!onEdit && (
                  <div className={"d-flex flex-column mt-3"}>
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      rows={4}
                      className={`form-input_field w-100 p-2`}
                      placeholder={"Ajouter une information complémentaire"}
                    />
                    <div className={"d-flex justify-content-start mt-2"}>
                      {" "}
                      <Button
                        disabled={
                          addComment.isLoading ||
                          modifyComment.isLoading ||
                          deleteComment.isLoading ||
                          !newComment.trim()
                        }
                        variant="primary"
                        size="sm"
                        onClick={() => addComment.mutate()}
                      >
                        {addComment.isLoading ? "Ajout..." : "Ajouter"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </Tab.Pane>

            <Tab.Pane eventKey="external">
              <div className="d-flex flex-column gap-3">
                {!comments ||
                comments.filter((c) => c.isExternal).length < 1 ? (
                  <p className="p-no-property mt-3">Aucun message</p>
                ) : (
                  <>
                    {comments
                      .filter((comment) => comment.isExternal)
                      .map((comment: Comment, index: number) => {
                        return (
                          <div key={index} className="d-flex flex-column gap-3">
                            <div className="d-flex gap-1 flex-column">
                              <div className={"d-flex"}>
                                <span>
                                  <i
                                    className="pi pi-envelope"
                                    style={{ color: "slateblue" }}
                                  ></i>
                                </span>
                                <span
                                  className={"text-primary mx-2"}
                                  style={{
                                    fontWeight: "bold",
                                  }}
                                >
                                  {comment.user.lastname}{" "}
                                  {comment.user.firstname}
                                </span>
                              </div>
                              <label
                                style={{
                                  fontSize: "0.8rem",
                                  color: "#6c757d",
                                }}
                              >
                                le {getDateFromIso(comment.createdAt)} à{" "}
                                {DateTime.fromISO(comment.createdAt).toFormat(
                                  "HH:mm"
                                )}
                              </label>
                            </div>
                            <div className={"position-relative"}>
                              <p>{comment.comment}</p>
                            </div>
                            <div className="border-bottom" />
                          </div>
                        );
                      })}
                  </>
                )}

                <div className={"d-flex flex-column mt-3"}>
                  <textarea
                    value={newExternalComment}
                    onChange={(e) => setNewExternalComment(e.target.value)}
                    rows={4}
                    className={`form-input_field w-100 p-2`}
                    placeholder={"Ajouter un message"}
                  />
                  <div className={"d-flex justify-content-start mt-2"}>
                    {" "}
                    <Button
                      disabled={
                        addComment.isLoading ||
                        modifyComment.isLoading ||
                        deleteComment.isLoading ||
                        !newExternalComment.trim()
                      }
                      variant="primary"
                      size="sm"
                      onClick={() => addComment.mutate()}
                    >
                      {addComment.isLoading ? "Ajout..." : "Ajouter"}
                    </Button>
                  </div>
                </div>
              </div>
            </Tab.Pane>
          </Tab.Content>
        </div>
      </Tab.Container>
    </>
  );
};

export default OrderComments;
